import { ThemeProvider } from "@mui/material/styles";
import type { AppProps } from "next/app";
import Head from "next/head";
import { usePathname } from "next/navigation";
import { useRouter } from "next/router";
import React, { useEffect } from "react";
import { useAuthContext } from "../context/AmplifyContext";
import { RutterConnectionsProvider } from "../context/RutterConnectionsContext";
import { useStoresContext } from "../context/StoresContext";
import { LoadingOverlay } from "./components/AppComponents/LoadingOverlay";
import useFetchWhitelabeling from "./hooks/useFetchWhitelabeling";
import useIsSwapAgency from "./hooks/useIsSwapAgency";
import useSettings from "./hooks/useSettings";
import { useWhitelabeling } from "./hooks/useWhitelabeling";
import { MixpanelTracking } from "./services/MixpanelService";
import { createCustomTheme } from "./theme";

export const DEFAULT_NAVBAR_COLOR = "#5664D2";
export const DEFAULT_ACCENT_COLOR = "#5664D2";
const DEFAULT_NAVBAR_TEXT_COLOR = "#fff";

const nonAuthedPages = [
  "/login",
  "/onboarding",
  "/register",
  "/",
  "/login/recovery",
  "/signin",
  "/link-expired",
  "/dev/unlink-agency",
  "/dev/apply-whitelabeling",
];

export default function Pages({ Component, pageProps }: AppProps) {
  const { stores, isStoresLoading, selectedStore } = useStoresContext();
  const router = useRouter();
  const pathName = usePathname();
  const { settings } = useSettings();
  const { authedUser, userAttributes } = useAuthContext();
  const mixpanelService = MixpanelTracking.getInstance();
  const email = userAttributes?.email;
  const sub = userAttributes?.sub;
  const name = userAttributes?.name;

  // redirect user if process onboarding store unfinished
  const noRedirectUrls = [
    "/close",
    "/signin?provider=google",
    "/signin?provider=facebook",
    "/signin",
  ];
  useEffect(() => {
    if (
      !isStoresLoading &&
      authedUser &&
      selectedStore?.isOnboarding &&
      !["/settings/account", "/settings/connections"].includes(router.pathname)
    ) {
      if (!noRedirectUrls.includes(router.pathname)) {
        router.replace({
          pathname: "/onboarding",
          query: router.query,
        });
      }
    }
  }, [selectedStore]);

  React.useEffect(() => {
    if (email && sub) {
      mixpanelService.identifyNewUser(sub);
      mixpanelService.setUser(email, name || "");
    }
  }, [email, mixpanelService, name, sub]);

  const { data: whitelabelingData, logo } = useWhitelabeling();
  useFetchWhitelabeling();
  const isSwapAgency = useIsSwapAgency();

  React.useEffect(() => {
    const accentColor = whitelabelingData?.response?.colors?.accent || DEFAULT_ACCENT_COLOR;
    const navbarColor = whitelabelingData?.response?.colors?.navigation || DEFAULT_NAVBAR_COLOR;
    const navbarTextColor = isSwapAgency ? "#000" : DEFAULT_NAVBAR_TEXT_COLOR;

    document.documentElement.style.setProperty("--primary", accentColor);
    document.documentElement.style.setProperty("--navbar", navbarColor);
    document.documentElement.style.setProperty("--navbar-text", navbarTextColor);
    document.querySelector("meta[name='theme-color']")?.setAttribute("content", navbarColor);
  }, [
    isSwapAgency,
    whitelabelingData?.response?.colors?.accent,
    whitelabelingData?.response?.colors?.navigation,
  ]);

  const theme = createCustomTheme({
    direction: settings.direction,
    responsiveFontSizes: settings.responsiveFontSizes,
    roundedCorners: settings.roundedCorners,
    theme: settings.theme,
    primaryColor: whitelabelingData?.response?.colors?.accent || DEFAULT_ACCENT_COLOR,
  });

  React.useEffect(() => {
    if (
      stores.length === 0 &&
      !isStoresLoading &&
      authedUser &&
      !nonAuthedPages.includes(pathName || "") &&
      !["/settings/account", "/settings/connections"].includes(router.pathname)
    ) {
      router.replace("/onboarding");
    }
  }, [authedUser, isStoresLoading, pathName, router, stores.length]);

  if (
    !nonAuthedPages.includes(pathName || "") &&
    router.pathname !== "/settings/account" &&
    (isStoresLoading || !selectedStore || stores.length === 0)
  ) {
    return <LoadingOverlay fullPage />;
  }

  if (
    !nonAuthedPages.includes(pathName || "") &&
    router.pathname !== "/settings/account" &&
    (whitelabelingData.isLoading || !whitelabelingData.isSuccess)
  ) {
    return <LoadingOverlay fullPage />;
  }

  return (
    <ThemeProvider theme={theme}>
      <Head>
        <meta name="viewport" content="initial-scale=1, width=device-width" />
        {!whitelabelingData.isLoading &&
          whitelabelingData.isSuccess &&
          !whitelabelingData.isLoadingPartnerId && (
            <link rel="icon" href={logo.smallSizeFavicon} type="image/png" />
          )}
      </Head>
      <RutterConnectionsProvider>
        <Component {...pageProps} />
      </RutterConnectionsProvider>
    </ThemeProvider>
  );
}
